import type { FeatureCollection } from 'geojson'

import { LocationVariantType } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import { MapCoordinates } from '../../types'

const MIN_POINTS_IN_POLYGON = 3

const getOrientation = (
   p: MapCoordinates,
   q: MapCoordinates,
   r: MapCoordinates
): 'collinear' | 'clockwise' | 'counterclockwise' => {
   const linearityValue =
      (q.lng - p.lng) * (r.lat - q.lat) - (q.lat - p.lat) * (r.lng - q.lng)

   if (linearityValue === 0) return 'collinear'
   return linearityValue > 0 ? 'clockwise' : 'counterclockwise'
}

const checkIsPointOnSegment = (
   p: MapCoordinates,
   q: MapCoordinates,
   r: MapCoordinates
): boolean =>
   q.lat <= Math.max(p.lat, r.lat) &&
   q.lat >= Math.min(p.lat, r.lat) &&
   q.lng <= Math.max(p.lng, r.lng) &&
   q.lng >= Math.min(p.lng, r.lng)

const doSegmentsIntersect = (
   p1: MapCoordinates,
   q1: MapCoordinates,
   p2: MapCoordinates,
   q2: MapCoordinates
): boolean => {
   const o1 = getOrientation(p1, q1, p2)
   const o2 = getOrientation(p1, q1, q2)
   const o3 = getOrientation(p2, q2, p1)
   const o4 = getOrientation(p2, q2, q1)

   // General case
   if (o1 !== o2 && o3 !== o4) return true

   // Special cases
   if (o1 === 'collinear' && checkIsPointOnSegment(p1, p2, q1)) return true
   if (o2 === 'collinear' && checkIsPointOnSegment(p1, q2, q1)) return true
   if (o3 === 'collinear' && checkIsPointOnSegment(p2, p1, q2)) return true
   if (o4 === 'collinear' && checkIsPointOnSegment(p2, q1, q2)) return true

   return false
}

export const isValidClosedAreaWithoutSelfIntersections = (
   coordinates: MapCoordinates[]
): boolean => {
   const hasValidNumberOfCoordinates =
      coordinates.length > MIN_POINTS_IN_POLYGON
   if (!hasValidNumberOfCoordinates) return false

   const firstPoint = coordinates.at(0)
   const lastPoint = coordinates.at(-1)
   const isFirstPointAndLastPointSame =
      firstPoint?.lat === lastPoint?.lat && firstPoint?.lng === lastPoint?.lng
   if (!isFirstPointAndLastPointSame) return false

   const edgeCount = coordinates.length - 1

   for (let i = 0; i < edgeCount - 1; i++) {
      for (let j = i + 2; j < edgeCount; j++) {
         if (i === 0 && j === edgeCount - 1) continue
         if (
            doSegmentsIntersect(
               coordinates[i],
               coordinates[i + 1],
               coordinates[j],
               coordinates[j + 1]
            )
         ) {
            return false
         }
      }
   }

   return true
}

export const convertPositionToCoordinates = (
   position: GeoJSON.Position
): MapCoordinates => ({ lng: position[0], lat: position[1] })

export const convertCoordinatesToPosition = (
   coordinates: MapCoordinates
): GeoJSON.Position => [coordinates.lng, coordinates.lat]

export const getGeoJsonBasedOnType = (
   type: LocationVariantType,
   coordinates: MapCoordinates[]
): FeatureCollection | null => {
   switch (type) {
      case 'POLYGON': {
         return {
            type: 'FeatureCollection',
            features: [
               {
                  type: 'Feature',
                  geometry: {
                     type: 'Polygon',
                     coordinates: [
                        coordinates.map(convertCoordinatesToPosition)
                     ]
                  },
                  properties: {}
               }
            ]
         }
      }
      case 'LINE': {
         return {
            type: 'FeatureCollection',
            features: [
               {
                  type: 'Feature',
                  geometry: {
                     type: 'LineString',
                     coordinates: coordinates.map(convertCoordinatesToPosition)
                  },
                  properties: {}
               }
            ]
         }
      }
      default:
         return null
   }
}
